import * as React from "react";
import IconReverseLeft from "@/icons/IconReverseLeft";
import IconReverseRight from "@/icons/IconReverseRight";
import IconTarget from "@/icons/IconTarget";
import { channelDefs } from "@/types";
import { ZoomIn, ZoomOut } from "@velaro/velaro-component-lib";

export function WorkflowEditorControls({
  scale,
  zoomIn,
  zoomOut,
  resetTransform,
  channelPerspective,
  onChannelPerspectiveChange,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
}: {
  scale: number;
  zoomIn(step: number): void;
  zoomOut(step: number): void;
  resetTransform(): void;
  channelPerspective?: string | null;
  onChannelPerspectiveChange?: (channel: string | null) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
}) {
  const iconClass = "text-slate-500 group-hover:text-primary";

  return (
    <div className="flex flex-col items-center justify-center absolute left-10 top-10 z-10 gap-6" style={{ width: "auto" }}>
      {/* Zoom control */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 flex items-center">
        <button
          onClick={() => zoomOut(0.1)}
          title="Zoom out"
          className="group w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors duration-200 ease-in-out rounded-l-lg"
          aria-label="Zoom out"
        >
          <ZoomOut className="text-slate-400 group-hover:text-primary" width={18} height={18} />
        </button>
        <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
        <button
          onClick={resetTransform}
          title="Reset zoom"
          className="px-2.5 h-10 flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors duration-200 ease-in-out text-slate-500 text-[11px] font-medium tabular-nums whitespace-nowrap select-none"
          aria-label="Reset zoom"
        >
          {`${Math.round(scale * 100)}%`}
        </button>
        <div className="w-px h-5 bg-slate-200 flex-shrink-0" />
        <button
          onClick={() => zoomIn(0.1)}
          title="Zoom in"
          className="group w-10 h-10 flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors duration-200 ease-in-out rounded-r-lg"
          aria-label="Zoom in"
        >
          <ZoomIn className="text-slate-400 group-hover:text-primary" width={18} height={18} />
        </button>
      </div>
      <Group>
        <Button onClick={resetTransform}>
          <IconTarget className={iconClass} />
        </Button>
      </Group>
      <Group>
        <Button onClick={onUndo} disabled={!canUndo} title="Undo">
          <IconReverseLeft className={iconClass} />
        </Button>
        <Border />
        <Button onClick={onRedo} disabled={!canRedo} title="Redo">
          <IconReverseRight className={iconClass} />
        </Button>
      </Group>
      {onChannelPerspectiveChange && (
        <Group>
          <div className="px-1.5 py-1.5">
            <select
              value={channelPerspective ?? ""}
              onChange={(e) =>
                onChannelPerspectiveChange(e.target.value || null)
              }
              className="text-[10px] text-slate-600 bg-transparent border-none outline-none cursor-pointer w-full text-center appearance-none"
              aria-label="Channel perspective"
              title="View workflow from a specific channel's perspective"
            >
              <option value="">All Channels</option>
              {Object.entries(channelDefs).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </Group>
      )}
    </div>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow-md border border-slate-200 flex-col justify-center items-center inline-flex">
      {children}
    </div>
  );
}

function Button({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      title={title}
      className={`group w-10 h-10 relative flex items-center justify-center transition-colors duration-200 ease-in-out ${
        disabled ? "opacity-30 cursor-default" : "cursor-pointer hover:bg-slate-50"
      }`}
    >
      {children}
    </div>
  );
}

function Border() {
  return <div className="w-10 h-px border border-slate-200"></div>;
}
